import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axiosInstance from '../api/axiosInstance';
import { jwtDecode } from 'jwt-decode';

function UnreadThreadsView() {
  const [threads, setThreads] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [markingAll, setMarkingAll] = useState(false);
  const [lastPostPages, setLastPostPages] = useState({});

  const token = localStorage.getItem('token');
  let currentUser = null;

  if (token) {
    try {
      currentUser = jwtDecode(token);
    } catch (e) {
      console.error('Token ungültig', e);
    }
  }

  // Helper function to calculate page number for a post
  const calculatePostPage = async (threadId, postId) => {
    try {
      const response = await axiosInstance.get(`/posts/thread/${threadId}/post/${postId}`);
      const postsPerPage = 20; // Should match backend limit
      const postIndex = response.data.index;
      return Math.ceil((postIndex + 1) / postsPerPage);
    } catch (error) {
      console.error('Error calculating post page:', error);
      return 1; // Fallback to page 1
    }
  };

  const fetchUnreadThreads = async (pageToLoad = 1) => {
    try {
      if (pageToLoad === 1) setLoading(true);
      else setLoadingMore(true);

      const res = await axiosInstance.get(`/thread-activity/unread?page=${pageToLoad}`);
      const newThreads = res.data.threads;
      const more = res.data.hasMore;

      // Debug logging for debugging unread issues
      if (currentUser?.isAdmin) {
        console.log('🔍 Unread API Debug:', {
          threads: newThreads?.length || 0,
          hasMore: more,
          pageToLoad,
          debugInfo: res.data.debug,
          response: res.data
        });
      }

      setThreads(prev => (pageToLoad === 1 ? newThreads : [...prev, ...newThreads]));
      setHasMore(more);
      
      // Calculate pages for last posts - optimized with Promise.all
      const pagesInfo = { ...lastPostPages };
      const pageCalculations = [];
      
      // Collect all page calculations for new threads
      for (const thread of newThreads) {
        if (thread.lastPost && !pagesInfo[thread._id]) {
          pageCalculations.push({
            threadId: thread._id,
            postId: thread.lastPost._id,
            promise: calculatePostPage(thread._id, thread.lastPost._id)
          });
        }
      }
      
      // Execute all calculations in parallel
      if (pageCalculations.length > 0) {
        const results = await Promise.allSettled(
          pageCalculations.map(calc => calc.promise)
        );
        
        // Process results
        pageCalculations.forEach((calc, index) => {
          const result = results[index];
          const page = result.status === 'fulfilled' ? result.value : 1;
          
          pagesInfo[calc.threadId] = {
            threadId: calc.threadId,
            postId: calc.postId,
            page: page,
          };
        });
        
        setLastPostPages(pagesInfo);
      }
      
      setLoading(false);
      setLoadingMore(false);
    } catch (err) {
      console.error('❌ Unread fetch error:', err);
      
      // Besseres Error-Handling
      if (err.response?.status === 401) {
        // Token abgelaufen - Umleitung zum Login
        localStorage.removeItem('token');
        window.location.href = '/login';
      } else {
        console.error('Unread fetch failed with status:', err.response?.status);
      }
      
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const markAllAsRead = async () => {
    try {
      setMarkingAll(true);
      const ids = threads.map(t => t._id);
      await axiosInstance.post('/thread-activity/mark-all-read', { threadIds: ids });
      
      // Reset state to allow for new unread threads to be fetched
      setThreads([]);
      setPage(1);
      setHasMore(true);
      setMarkingAll(false);
      
      // Refresh to check for any new unread threads
      fetchUnreadThreads(1);
    } catch (err) {
      console.error(err);
      setMarkingAll(false);
    }
  };

  useEffect(() => {
    fetchUnreadThreads(1);
  }, []);

  // Add a refresh button for manual refresh instead of automatic
  const handleRefresh = () => {
    setPage(1);
    setHasMore(true);
    fetchUnreadThreads(1);
  };

  const loadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchUnreadThreads(nextPage);
  };

  // Generate unread thread URL that goes to first unread post
  const getUnreadThreadUrl = (thread) => {
    // Navigate to last page initially, then the JavaScript will handle finding the actual unread post
    const lastPage = Math.ceil(thread.postCount / 20) || 1;
    return `/thread/${thread._id}?page=${lastPage}&unread=true`;
  };

  const getLastPostUrl = (thread) => {
    const info = lastPostPages[thread._id];
    if (!info) return `/thread/${thread._id}#lastpost`;
    return `/thread/${info.threadId}?page=${info.page}#post-${info.postId}`;
  };

  const truncate = (str, length) => 
    str.length > length ? str.substring(0, length) + '...' : str;

  const formatDate = (dateStr) =>
    new Date(dateStr).toLocaleString('de-DE', {
      dateStyle: 'short',
      timeStyle: 'short',
    });

  return (
    <div className="max-w-6xl mx-auto px-2 sm:px-4">
      <div className="text-center mb-12">
        <h1 className="text-5xl sm:text-6xl text-white font-marker mb-4 animate-fade-in">
          Ungelesene Threads
        </h1>
        <div className="flex justify-center gap-4 flex-wrap">
          {threads.length > 0 && (
            <button
              onClick={markAllAsRead}
              disabled={markingAll}
              className="btn-modern disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {markingAll ? (
                <span className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Markiere alle...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <span>✅</span>
                  Alle als gelesen markieren
                </span>
              )}
            </button>
          )}
          <button
            onClick={handleRefresh}
            disabled={loading}
            className="btn-modern disabled:opacity-50 disabled:cursor-not-allowed bg-gray-600 hover:bg-gray-700"
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
                Aktualisiere...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <span>🔄</span>
                Aktualisieren
              </span>
            )}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center mt-12">
          <div className="modern-card p-8">
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
              <span className="ml-3 text-lg text-gray-600 dark:text-gray-300">Lade ungelesene Threads...</span>
            </div>
          </div>
        </div>
      ) : threads.length === 0 ? (
        <div className="modern-card p-8 text-center">
          <div className="text-gray-500 dark:text-gray-400 text-lg">
            Keine ungelesenen Threads gefunden 🎉
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          {threads.map((thread, index) => (
            <div
              key={thread._id}
              className="modern-card group hover:scale-[1.02] transition-all duration-300"
              style={{ animationDelay: `${index * 50}ms` }}
            >
              <div className="p-6">
                <div className="flex flex-col lg:flex-row lg:justify-between gap-4">
                  <div className="flex-1">
                    <Link
                      to={getUnreadThreadUrl(thread)}
                      className="font-semibold text-xl hover:text-primary transition-colors duration-200 text-gray-800 dark:text-white group-hover:text-primary block mb-2"
                    >
                      {truncate(thread.title, 80)}
                    </Link>
                    <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400 mb-3">
                      <span className="flex items-center gap-1">
                        <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                        von {thread.author?.username || 'Unbekannt'}
                      </span>
                      <span className="flex items-center gap-1">
                        <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                        {formatDate(thread.createdAt)}
                      </span>
                    </div>
                    <div className="inline-flex items-center gap-2 bg-gradient-to-r from-primary to-primary-focus text-white text-sm font-semibold px-4 py-2 rounded-full">
                      <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
                      {thread.unreadCount} ungelesene Beiträge
                    </div>
                  </div>
                  <div className="flex-shrink-0 text-right min-w-[200px]">
                    <div className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-2">
                      Letzter Beitrag:
                    </div>
                    {thread.lastPost ? (
                      <Link
                        to={getLastPostUrl(thread)}
                        className="hover:text-primary transition-colors duration-200 block"
                      >
                        <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">
                          von {thread.lastPost.author?.username || '—'}
                        </div>
                        <div className="text-xs text-gray-400">
                          {formatDate(thread.lastPost.createdAt)}
                        </div>
                        <div className="text-xs text-primary font-medium mt-1">
                          Zum letzten Beitrag →
                        </div>
                      </Link>
                    ) : (
                      <div className="text-sm text-gray-500 dark:text-gray-400 italic">
                        Noch keine Antworten
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}

          {hasMore && (
            <div className="flex justify-center mt-8">
              <button
                onClick={loadMore}
                disabled={loadingMore}
                className="modern-btn-primary px-8 py-3 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loadingMore ? (
                  <span className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Lade weitere...
                  </span>
                ) : (
                  'Weitere laden'
                )}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default UnreadThreadsView;
